import { createSlice, createSelector } from '@reduxjs/toolkit';
import _, { filter, find } from 'lodash';
import { FieldModel } from '../models/form/field';
import { CultureTextModel, FormModel } from '../models/form/form';
import { Step } from '../models/form/step';
import { Section } from '../models/form/section';
import { RootState } from '../store';
import { FieldType } from '../models/form/fieldType';
import { addLanguage, removeLanguage } from '../utilities/CultureUtilities';

export enum formAdminErrors {
  ADD_FIELD_IN_EMPTY_STEP = 'ADD_FIELD_IN_EMPTY_STEP',
  ADD_FIELD_IN_EMPTY_SECTION = 'ADD_FIELD_IN_EMPTY_SECTION',
}

interface FormAdminState {
  form: FormModel | null;
  selectedField: string | undefined;
  selectedStep: string | undefined;
  fieldsShown: boolean;
  propertiesShown: boolean;
  errors: formAdminErrors[];
  selectedLanguage: string | undefined;
  languageTouched: { [id: string]: { [culture: string]: boolean } };
  added: boolean;
}

const forEachCultureText = (
  form: FormModel,
  callback: (cultureModel: CultureTextModel[] | string) => CultureTextModel[] | string,
) => {
  form.name = callback(form.name);
  if (form.emailContent) {
    form.emailContent.content = callback(form.emailContent.content);
  }

  for (const step of form.content.steps) {
    step.name = callback(step.name);
    for (const section of step.sections) {
      section.name = callback(section.name);

      for (const field of section.fields) {
        field.text = callback(field.text);
        switch (field.type) {
          case FieldType.Birthdate:
          case FieldType.Date:
          case FieldType.Numeric:
          case FieldType.Time:
          case FieldType.Datetime:
            field.placeholder = callback(field.placeholder);
            break;

          case FieldType.Text:
          case FieldType.EmailAddress:
          case FieldType.LongText:
          case FieldType.WebLink:
            field.placeholder = callback(field.placeholder);
            field.defaultValue = callback(field.defaultValue);
            break;

          case FieldType.StaticText:
            field.value = callback(field.value);
            break;

          case FieldType.EmbeddedPhoto:
            field.defaultValue = callback(field.defaultValue);
            field.alt = callback(field.alt);
            break;
          case FieldType.Video:
            field.defaultValue = callback(field.defaultValue);
            break;
          case FieldType.DocumentViewer:
            field.defaultValue = callback(field.defaultValue);
            field.documentName = callback(field.documentName);
            break;

          case FieldType.Select:
            field.placeholder = callback(field.placeholder);
            for (const option of field.options) {
              option.text = callback(option.text);
            }
            break;
          case FieldType.OptionList:
            for (const option of field.options) {
              option.text = callback(option.text);
            }
            break;
          case FieldType.PaymentOptionList:
            for (const option of field.options) {
              option.text = callback(option.text);
              option.description = callback(option.description);
            }
            break;
          case FieldType.PaymentChoiceList:
            for (const option of field.choices) {
              option.text = callback(option.text);
              option.description = callback(option.description);
            }
            break;
        }
      }
    }
  }
};

export const formAdminSlice = createSlice({
  name: 'formadmin',
  initialState: {
    form: null,
    selectedField: undefined,
    selectedStep: undefined,
    fieldsShown: true,
    propertiesShown: false,
    errors: [],
    selectedLanguage: undefined,
    languageTouched: {},
    added: false,
  } as FormAdminState,
  reducers: {
    setRules: (state, action) => {
      if (state.form) {
        state.form.content.rules = action.payload;
      }
    },
    setForm: (state, action) => {
      state.form = action.payload;
      if (state.form) {
        const steps = state.form.content.steps;
        if (steps && steps.length > 0) {
          state.selectedStep = state.form.content.steps[0].id;
        }
      }
    },
    setFormName: (state, action) => {
      if (state.form) {
        state.form.name = action.payload;
      }
    },
    setRecaptchaEnabled: (state, action) => {
      if (state.form) {
        state.form.recaptchaEnabled = action.payload;
      }
    },
    setPaymentEnabled: (state, action) => {
      if (state.form) {
        state.form.paymentEnabled = action.payload;
      }
    },
    setPaymentOptional: (state, action) => {
      if (state.form) {
        state.form.paymentOptional = action.payload;
      }
    },
    setFixedAmount: (state, action) => {
      if (state.form) {
        state.form.fixedAmount = action.payload;
      }
    },
    setFormLimit: (state, action) => {
      if (state.form) {
        if (action.payload != null) {
          state.form.submissionLimit = action.payload;
        } else {
          state.form.submissionLimit = null;
        }
      }
    },
    setStep: (state, action) => {
      if (state.form) {
        const formTemp = { ...state.form };
        for (let i = 0; i < formTemp.content.steps.length; i++) {
          if (formTemp.content.steps[i].id == action.payload.id) {
            formTemp.content.steps[i] = action.payload;
          }
        }
        state.form = formTemp;
      }
    },
    setSelectedField: (state, action) => {
      if (action.payload) {
        state.selectedField = action.payload.id;
      }
    },
    setSelectedStep: (state, action) => {
      if (action.payload) {
        state.selectedStep = action.payload.id;
      }
    },
    setSelectedStepById: (state, action) => {
      state.selectedStep = action.payload;
    },
    setFieldsShown: (state, action) => {
      state.fieldsShown = action.payload;
    },
    setPropertiesShown: (state, action) => {
      state.propertiesShown = action.payload;
    },
    showFields: (state) => {
      state.propertiesShown = false;
      state.fieldsShown = true;
    },
    showProperties: (state) => {
      state.propertiesShown = true;
      state.fieldsShown = false;
    },
    setSelectedFieldById: (state, action) => {
      state.selectedField = action.payload;
    },
    setField: (state, action) => {
      const formTemp = Object.assign({}, state.form);
      if (state.selectedStep) {
        const step = find(formTemp.content.steps, { id: state.selectedStep });
        if (step) {
          for (const section of step.sections) {
            for (let i = 0; i < section.fields.length; i++) {
              if (section.fields[i].id === action.payload.id) {
                section.fields[i] = action.payload;
                state.selectedField = action.payload.id;
                return;
              }
            }
          }
        }
      }
    },
    addField: (state, action) => {
      if (state.form && state.selectedStep) {
        const field = action.payload;
        const step = find(state.form.content.steps, { id: state.selectedStep });
        if ((step && step.sections.length === 0) || !step) {
          state.errors.push(formAdminErrors.ADD_FIELD_IN_EMPTY_SECTION);
        } else if (step) {
          const formTemp = { ...state.form };
          const tempFieldContainer: FieldModel = {
            ...field,
            size: 12,
          };

          const section = step.sections[step.sections.length - 1];

          section.fields.push({ ...tempFieldContainer });

          state.form = formTemp;
          state.selectedField = section.fields[section.fields.length - 1].id;
        }
      } else {
        state.errors.push(formAdminErrors.ADD_FIELD_IN_EMPTY_STEP);
      }
      console.log(state.form);
    },
    deleteField: (state, action) => {
      const { id } = action.payload;
      if (state.form && state.selectedStep) {
        const formTemp = { ...state.form };
        const step = find(formTemp.content.steps, { id: state.selectedStep });

        if (step) {
          for (const section of step.sections) {
            section.fields = filter(section.fields, (field) => field.id != id);
            state.selectedField = undefined;
          }
        }
      }
    },
    updateSelectedField: (state, action) => {
      if (state.form && state.selectedStep) {
        const formTemp = Object.assign({}, state.form);
        const step = find(formTemp.content.steps, { id: state.selectedStep });

        if (step) {
          for (const section of step.sections) {
            for (let i = 0; i < section.fields.length; i++) {
              if (section.fields[i].id === action.payload.id) {
                section.fields[i] = action.payload;
              }
            }
          }
        }
      }
    },
    deleteStep: (state, action) => {
      const { id } = action.payload;
      if (state.form) {
        const formTemp = { ...state.form };

        formTemp.content.steps = filter(formTemp.content.steps, (step) => step.id != id);
        state.form = formTemp;
        state.selectedStep = formTemp.content.steps.length > 0 ? formTemp.content.steps[0].id : undefined;
        state.selectedField = undefined;
      }
    },
    addStep: (state, action) => {
      if (state.form) {
        const stepName = action.payload;
        const formTemp = { ...state.form };
        const newStep = new Step('New Step');
        newStep.name = stepName;
        formTemp.content.steps.push({ ...newStep });
        state.form = formTemp;
        state.selectedStep = formTemp.content.steps[formTemp.content.steps.length - 1].id;
        state.selectedField = undefined;
      }
    },
    updateStep: (state, action) => {
      if (state.form) {
        const { id, name } = action.payload;

        for (const step of state.form.content.steps) {
          if (step.id === id) {
            step.name = name;
          }
        }
      }
    },
    addSection: (state, action) => {
      if (state.form && state.selectedStep) {
        const { name, multiple } = action.payload;

        const formTemp = { ...state.form };
        const step = find(formTemp.content.steps, { id: state.selectedStep });

        if (step) {
          const newSection = new Section(name);
          newSection.multiple = multiple;
          step.sections.push({ ...newSection });

          state.form = formTemp;
        }
      }
    },
    updateSection: (state, action) => {
      if (state.form && state.selectedStep) {
        const updatedSection = action.payload;

        const formTemp = { ...state.form };
        const step = find(formTemp.content.steps, { id: state.selectedStep });

        if (step) {
          for (const section of step.sections) {
            if (section.id === updatedSection.id) {
              Object.assign(section, updatedSection);
            }
          }

          state.form = formTemp;
        }
      }
    },
    deleteSection: (state, action) => {
      const { id } = action.payload;
      if (state.form && state.selectedStep) {
        const formTemp = { ...state.form };
        const step = find(formTemp.content.steps, { id: state.selectedStep });

        if (step) {
          step.sections = filter(step.sections, (section) => section.id != id);

          state.form = formTemp;
        }
      }
    },
    removeError: (state, action) => {
      state.errors.splice(action.payload, 1);
    },
    updatePaymentModes: (state, action) => {
      if (state.form) {
        if (!state.form.paymentModes) {
          state.form.paymentModes = { modes: [], applyTo: [] };
        }

        state.form.paymentModes.modes = action.payload;
      }
    },
    updateDiscountCodes: (state, action) => {
      if (state.form) {
        if (!state.form.discountCodes) {
          state.form.discountCodes = [];
        }

        state.form.discountCodes = action.payload;
      }
    },
    setSelectedLanguage: (state, action) => {
      if (action.payload) {
        state.selectedLanguage = action.payload;
        state.added = false;
      }
    },
    setLocalizations: (state, action) => {
      if (action.payload && state.form) {
        state.form.localizations = action.payload;
      }
    },
    addLocalization: (state, action) => {
      if (action.payload && state.form) {
        const fallbackLocalization = state.form.localizations[0];
        state.form.localizations = [...state.form.localizations, action.payload];

        forEachCultureText(state.form, (cultureText) =>
          addLanguage(cultureText, state.selectedLanguage || fallbackLocalization, action.payload),
        );
        state.added = true;
        state.selectedLanguage = action.payload;
      }
    },
    removeLocalization: (state, action) => {
      if (action.payload && state.form) {
        state.added = false;
        state.form.localizations = (state.form.localizations || []).filter((val) => val !== action.payload);

        forEachCultureText(state.form, (cultureText) => {
          if (_.isArray(cultureText)) {
            return removeLanguage(cultureText, action.payload);
          }

          return cultureText;
        });

        state.selectedLanguage = state.form.localizations[0];
      }
    },
    addLanguageTouched: (state, action) => {
      if (action.payload && !state.added) {
        const { id, culture } = action.payload;

        if (!state.languageTouched[id]) {
          state.languageTouched[id] = {};
        }

        state.languageTouched[id][culture] = true;
        console.log(JSON.stringify(state.languageTouched));
      }
    },
    setLanguageTouched: (state, action) => {
      state.languageTouched = action.payload;
    },
  },
});

export const selectedStepSelector = createSelector(
  [(state: RootState) => state.formAdmin.form, (state) => state.formAdmin.selectedStep],
  (form, selectedStep) => {
    if (form && selectedStep) {
      return find(form.content.steps, { id: selectedStep });
    }

    return;
  },
);

export const selectedFieldSelector = createSelector(
  [(state: RootState) => state.formAdmin.form, (state) => state.formAdmin.selectedField],
  (form, selectedField) => {
    if (form && selectedField) {
      for (const step of form.content.steps) {
        for (const section of step.sections) {
          for (const field of section.fields) {
            if (field.id === selectedField) {
              return field;
            }
          }
        }
      }
    }

    return;
  },
);

export const hasPaymentSelector = createSelector(
  [(state: RootState) => state.formAdmin.form?.content.steps],
  (steps) => {
    for (const step of steps || []) {
      for (const section of step.sections) {
        for (const field of section.fields) {
          if (
            field.type === FieldType.PaymentChoiceList ||
            field.type === FieldType.PaymentOptionList ||
            field.type === FieldType.Products
          ) {
            return true;
          }
        }
      }
    }
    return false;
  },
);

export const languageWarningsSelector = (id): any =>
  createSelector([(state: RootState) => state.formAdmin.languageTouched], (languageTouched) => {
    return languageTouched[id];
  });

export const {
  setRules,
  setForm,
  setFormName,
  setRecaptchaEnabled,
  setPaymentEnabled,
  setPaymentOptional,
  setFixedAmount,
  setFormLimit,
  setStep,
  setSelectedField,
  setSelectedStep,
  setSelectedStepById,
  setFieldsShown,
  setPropertiesShown,
  showFields,
  showProperties,
  setSelectedFieldById,
  setField,
  addField,
  deleteField,
  updateSelectedField,
  deleteStep,
  addStep,
  updateStep,
  addSection,
  updateSection,
  deleteSection,
  removeError,
  updatePaymentModes,
  updateDiscountCodes,
  setSelectedLanguage,
  setLocalizations,
  addLocalization,
  removeLocalization,
  addLanguageTouched,
  setLanguageTouched,
} = formAdminSlice.actions;

export default formAdminSlice.reducer;
